import type { IEmployeeApiPort } from '@personnel/application/ports/IEmployeeApiPort';
import type { EmployeeBulkDeleteDto, EmployeeBulkDeleteResponseDto } from '@personnel/application/dtos';

/**
 * Use Case for bulk deleting employees
 *
 * Orchestrates the bulk employee deletion flow by delegating to the API port.
 *
 * @param {Object} props - Use case dependencies
 * @param {IEmployeeApiPort} props.apiPort - Port for API operations
 * @returns {Object} Use case executor with execute method
 *
 * @example
 * const useCase = bulkDeleteEmployeesUseCase({ apiPort: employeeRepository });
 * const result = await useCase.execute({ uuids: ['uuid-1', 'uuid-2'] });
 * console.log(result.deletedCount, result.deletedUuids);
 */
export const bulkDeleteEmployeesUseCase = (props: { apiPort: IEmployeeApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes the bulk employee deletion
   *
   * @param {EmployeeBulkDeleteDto} request - Bulk deletion request with UUIDs
   * @returns {Promise<EmployeeBulkDeleteResponseDto>} Deletion result with success status and deleted count
   */
  const execute = async (request: EmployeeBulkDeleteDto): Promise<EmployeeBulkDeleteResponseDto> => {
    return apiPort.bulkDeleteEmployees(request);
  };

  return { execute };
};
